<template lang="pug">
.user-details(v-loading="loading")
  h2 {{ $t("profile") }}

  AdminForm.profile-settings-form(
    :model="profileSettingsForm",
    labelWidth="25ch"
  )
    AdminFormRow
      AdminFormFullWidthField(
        :label="$t('profile_image')",
        prop="profilePicture",
        control-id="profile-picture-upload",
        span="full"
      )
        template(#default="field")
          .profile-picture-settings
            UserAvatar(:size="32", :user="avatarUser")
            el-button(
              native-type="button",
              :disabled="field.disabled",
              @click="openProfilePictureFileDialog"
            ) {{ $t("upload_picture") }}
            input#profile-picture-upload.visually-hidden(
              ref="profilePictureInput",
              type="file",
              tabindex="-1",
              accept="image/png, image/jpeg, image/jpg, image/gif, image/webp, image/bmp, image/tiff",
              :aria-describedby="field.ariaDescribedby",
              :aria-invalid="field.ariaInvalid",
              :disabled="field.disabled",
              @change="uploadUserProfile"
            )
            el-button(@click="cameraVisible = true") {{ $t("take_picture") }}
            el-button(
              v-loading="deletePictureLoading",
              type="danger",
              :disabled="deletePictureLoading",
              @click="deleteProfilePicture"
            ) {{ $t("delete") }}
      AdminFormFullWidthField(
        :label="$t('defaultLanguage')",
        prop="langCode",
        control-id="profile-language"
      )
        template(#default="field")
          LanguageSelect#profile-language(
            v-model="currentLang",
            :aria-describedby="field.ariaDescribedby",
            :aria-invalid="field.ariaInvalid",
            :aria-required="field.ariaRequired",
            :disabled="field.disabled"
          )
      AdminFormFullWidthField(
        v-if="$getSetting('showMarketingConsent') === true",
        :label="$t('consentingToMarketing')",
        prop="marketingConsent",
        control-id="profile-marketing-consent"
      )
        template(#default="field")
          CheckboxInput#profile-marketing-consent(
            :model-value="userLoadState.result?.marketingConsent ?? false",
            :aria-describedby="field.ariaDescribedby",
            :aria-invalid="field.ariaInvalid",
            :aria-required="field.ariaRequired",
            :disabled="field.disabled || userLoadState.loading",
            show-external-state,
            size="large",
            @update:model-value="setMarketingConsent"
          )

  .profile-fields-section
    AdminForm.profile-details-form(
      ref="profileFormRef",
      :model="editForm",
      :rules="profileRules",
      :disabled="saveLoading",
      @submit="saveProfile"
    )
      template(
        v-for="(section, sectionIndex) in profileSections",
        :key="sectionIndex"
      )
        h3.admin-form-section-title(v-if="section.label") {{ $t(section.label) }}
        AdminFormRow(
          v-for="(fields, rowIndex) in section.fieldRows",
          :key="`${sectionIndex}-${rowIndex}`"
        )
          AdminFormField(
            v-for="profileField in fields",
            :key="profileField.field",
            :label="$t(profileField.label)",
            :prop="profileField.field",
            :required="profileField.required",
            :allow-null="profileField.registrationField.nullable === true",
            :readonly="!isEditing",
            :control-id="isEditing ? profileFieldControlId(profileField.field) : undefined",
            field-class="el-table__row"
          )
            template(#default="field")
              template(v-if="!isEditing")
                CountryView(
                  v-if="profileField.inputType === 'country' && profileField.value",
                  :code="profileField.value"
                )
                span(v-else) {{ profileField.value }}
              template(v-else)
                CustomUserField(
                  :user="profileEditingUser",
                  :field="profileField.registrationField",
                  :control-id="profileFieldControlId(profileField.field)",
                  :aria-describedby="field.ariaDescribedby",
                  :aria-invalid="field.ariaInvalid",
                  :aria-required="field.ariaRequired",
                  :disabled="field.disabled || !profileField.editable",
                  :show-address-details="false",
                  @update:user="updateProfileEditingUser"
                )
      template(#actions)
        .profile-fields-header(v-if="hasEditableFields && isEditing")
          el-button(
            type="primary",
            :loading="saveLoading",
            @click="submitProfileForm"
          ) {{ $t("save") }}
          el-button(@click="cancelEdit") {{ $t("cancel") }}
    .profile-fields-header(v-if="hasEditableFields && !isEditing")
      el-button(text, type="primary", :icon="'Edit'", @click="startEditing") {{ $t("edit") }}

  modal(v-if="cameraVisible", @close="cameraVisible = false")
    template(#title) {{ $t("take_picture") }}
    Camera.mb-1(ref="camera", :aria-label="$t('take_picture')")
    template(#footer)
      el-button(type="primary", @click="takePicture") {{ $t("take_picture") }}
</template>

<script setup lang="ts">
import AdminForm from "@/components/elements/admin-form/AdminForm.vue";
import AdminFormField from "@/components/elements/admin-form/AdminFormField.vue";
import AdminFormRow from "@/components/elements/admin-form/AdminFormRow.vue";
import LanguageSelect from "@/components/elements/LanguageSelect.vue";
import CheckboxInput from "@/components/elements/CheckboxInput.vue";
import CountryView from "@/components/elements/CountryPicker/CountryView.vue";
import UserAvatar from "@/components/elements/UserAvatar.vue";
import CustomUserField from "@/components/auth/components/CustomUserField.vue";
import { getSetting } from "@/getSettings";
import {
  stateError,
  stateInitial,
  stateLoaded,
  stateLoading,
  type LoadState,
} from "@/store/loadingStates";
import formatDate from "@/utils/formatDate";
import {
  Accounts,
  type AccountsTypes,
  SystemConfiguration,
} from "@inctec/elearning-ui-client";
import type { FormRules } from "element-plus";
import { computed, onMounted, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import Camera from "./Camera.vue";
import type { RegistrationField } from "@/components/user/userData";
import { createRegistrationFieldRules } from "@/components/user/registrationValidation";

type InputType = "text" | "email" | "date" | "tel" | "country";

type FieldMeta = {
  label: string;
  isAttribute: boolean;
  inputType?: InputType;
  legacyAlias?: string;
};

type UserStateProfile = {
  username?: string;
  email?: string;
  qGate?: Record<string, unknown>;
  extras?: { attributes?: Record<string, unknown> };
};

type UserIdentity = { id: number };

type ProfileField = {
  field: string;
  label: string;
  inputType: InputType;
  isAttribute: boolean;
  value: string;
  editable: boolean;
  required: boolean;
  registrationField: RegistrationField;
};

type ProfileSection = {
  label?: string;
  fieldRows: ProfileField[][];
};

type CameraInstance = { capture: () => string };

const FIELD_METADATA: Record<string, FieldMeta> = {
  username: { label: "username", isAttribute: false },
  email: { label: "email", isAttribute: false, inputType: "email" },
  title: { label: "title", isAttribute: true },
  phone: { label: "phoneNumber", isAttribute: true, inputType: "tel" },
  firstname: { label: "firstname", isAttribute: true },
  lastname: { label: "lastname", isAttribute: true },
  address: { label: "address", isAttribute: true, legacyAlias: "address1" },
  city: { label: "city", isAttribute: true, legacyAlias: "address2" },
  zipCode: { label: "zipCode", isAttribute: true },
  country: { label: "country", isAttribute: true, inputType: "country" },
  birthdate: { label: "birthday", isAttribute: true, inputType: "date" },
  birthyear: { label: "birthyear", isAttribute: true },
  birthplace: { label: "birthplace", isAttribute: true },
  nationality: { label: "nationality", isAttribute: true },
  company: { label: "company", isAttribute: true },
  department: { label: "department", isAttribute: true },
  customId: { label: "customId", isAttribute: true },
  notes: { label: "userNotes", isAttribute: true },
  emergencyContact: { label: "emergencyContact", isAttribute: true },
  emergencyContactPhone: {
    label: "emergencyContactPhone",
    isAttribute: true,
    inputType: "tel",
  },
  subcontractor: { label: "subcontractor", isAttribute: true },
  car: { label: "car", isAttribute: true },
  carPlate: { label: "carPlate", isAttribute: true },
  workPermit: { label: "workPermit", isAttribute: true },
  socialSecurity: { label: "socialSecurity", isAttribute: true },
};

const LEGACY_ALIAS_FIELDS = new Set(["address1", "address2"]);

function registrationField(
  label: string,
  type: string,
  property: string,
  attributes: boolean,
  mandatory: boolean,
  inputType?: string,
): RegistrationField {
  return { label, type, property, attributes, mandatory, step: 0, inputType };
}

const store = useStore();
const { t, locale } = useI18n();
const camera = ref<CameraInstance>();
const profilePictureInput = ref<HTMLInputElement>();
const profileFormRef = ref<InstanceType<typeof AdminForm>>();
const cameraVisible = ref(false);
const deletePictureLoading = ref(false);
const saveLoading = ref(false);
const isEditing = ref(false);
const userEditableFields = ref<Record<string, boolean>>({});
const profileEditingUser = ref<Partial<AccountsTypes.User>>({
  attributes: {},
});
const editFormInitial = ref<Record<string, unknown>>({});
const profileSettingsForm = reactive<Record<string, unknown>>({});
const userLoadState = ref<LoadState<AccountsTypes.User | null>>({
  ...stateInitial,
  result: null,
});

const userData = computed<UserIdentity>(
  () => store.getters.getUserData as UserIdentity,
);
const userStateLoadingState = computed<LoadState<UserStateProfile | null>>(
  () =>
    store.getters[
      "user/userStateLoadingState"
    ] as LoadState<UserStateProfile | null>,
);
const loading = computed(() => userStateLoadingState.value.loading);
const profile = computed(() => userStateLoadingState.value.result);
const baseAttributes = computed<Record<string, unknown>>(
  () => profile.value?.extras?.attributes ?? {},
);
const qGateValues = computed<Record<string, unknown>>(
  () => profile.value?.qGate ?? {},
);
// The user-state endpoint uses a partial shape, while UserAvatar expects the
// full account-client type. It only reads id, username, and attributes here.
const avatarUser = computed(
  () =>
    ({
      ...(profile.value ?? {}),
      attributes: baseAttributes.value,
    }) as unknown as AccountsTypes.User,
);
const hasEditableFields = computed(() =>
  Object.entries(userEditableFields.value).some(
    ([field, editable]) => editable && allowedFields.value.has(field),
  ),
);

const registrationFields = computed<RegistrationField[]>(() => {
  const details = getSetting("registrationRequiredDetails");
  const fields: RegistrationField[] = [
    registrationField(
      "username",
      "text",
      "username",
      false,
      details.username?.mandatory ?? false,
    ),
    registrationField(
      "email",
      "text",
      "email",
      false,
      details.email?.mandatory ?? false,
      "email",
    ),
  ];

  if (details.title?.request) {
    fields.push(
      registrationField(
        "title",
        "text",
        "title",
        true,
        details.title.mandatory,
      ),
    );
  }
  if (details.firstname?.request) {
    fields.push(
      registrationField(
        "firstname",
        "text",
        "firstname",
        true,
        details.firstname.mandatory,
      ),
    );
  }
  if (details.lastname?.request) {
    fields.push(
      registrationField(
        "lastname",
        "text",
        "lastname",
        true,
        details.lastname.mandatory,
      ),
    );
  }
  if (details.birthday?.request) {
    fields.push(
      registrationField(
        "birthday",
        "date",
        "birthdate",
        true,
        details.birthday.mandatory,
      ),
    );
  }
  if (details.birthyear?.request) {
    fields.push(
      registrationField(
        "birthyear",
        "year",
        "birthyear",
        true,
        details.birthyear.mandatory,
      ),
    );
  }
  if (details.address?.request) {
    fields.push(
      registrationField(
        "address",
        "address",
        "address",
        true,
        details.address.mandatory,
      ),
    );
  }
  if (details.phone?.request) {
    fields.push(
      registrationField(
        "phoneNumber",
        "text",
        "phone",
        true,
        details.phone.mandatory,
        "tel",
      ),
    );
  }
  if (details.company?.request) {
    fields.push(
      registrationField(
        "company",
        "text",
        "company",
        true,
        details.company.mandatory,
      ),
    );
  }
  if (details.emergencyContact?.request) {
    fields.push(
      registrationField(
        "emergencyContact",
        "text",
        "emergencyContact",
        true,
        details.emergencyContact.mandatory,
      ),
      registrationField(
        "emergencyContactPhone",
        "text",
        "emergencyContactPhone",
        true,
        details.emergencyContact.mandatory,
        "tel",
      ),
    );
  }

  return [...fields, ...getSetting("customRegistrationFields").fields];
});

const registrationFieldsByProperty = computed<
  Record<string, RegistrationField>
>(() =>
  Object.fromEntries(
    registrationFields.value.map((field) => [field.property, field]),
  ),
);

const editForm = computed<Record<string, unknown>>(() => ({
  ...profileEditingUser.value,
  ...profileEditingUser.value.attributes,
}));

const fieldMetadata = computed<Record<string, FieldMeta>>(() => {
  const metadata: Record<string, FieldMeta> = { ...FIELD_METADATA };

  for (const field of getSetting("customRegistrationFields").fields) {
    if (!metadata[field.property]) {
      metadata[field.property] = {
        label: field.label,
        isAttribute: field.attributes ?? true,
        inputType: toInputType(field.inputType ?? field.type),
      };
    }
  }
  return metadata;
});

const allowedFields = computed<Set<string>>(() => {
  return new Set(registrationFields.value.map((field) => field.property));
});

const hasRegistrationSteps = getSetting("hasRegistrationSteps");
const registrationSteps = getSetting("registrationSteps");

const requiredFields = computed<Set<string>>(() => {
  const required = new Set(
    registrationFields.value
      .filter((field) => field.mandatory)
      .map((field) => field.property),
  );
  return required;
});

const visibleFields = computed<ProfileField[]>(() => {
  if (!userStateLoadingState.value.loaded || !profile.value) return [];

  const metadata = fieldMetadata.value;
  const orderedFields = Object.keys(metadata).filter((field) =>
    allowedFields.value.has(field),
  );

  return orderedFields.map((field): ProfileField => {
    const metadataForField = metadata[field];
    const isAttribute = metadataForField?.isAttribute ?? true;
    const inputType = metadataForField?.inputType ?? "text";
    const configuredRegistrationField =
      registrationFieldsByProperty.value[field];
    const qGateValue = qGateValues.value[field];
    const rawValue =
      qGateValue !== undefined
        ? qGateValue
        : isAttribute
          ? (baseAttributes.value[field] ??
            (metadataForField?.legacyAlias
              ? baseAttributes.value[metadataForField.legacyAlias]
              : undefined))
          : (profile.value as Record<string, unknown>)[field];
    const stringValue = toStringValue(rawValue);

    return {
      field,
      label: metadataForField?.label ?? field,
      inputType,
      isAttribute,
      value:
        inputType === "date" && stringValue
          ? formatDate(stringValue)
          : stringValue,
      editable:
        allowedFields.value.has(field) &&
        userEditableFields.value[field] === true,
      required: requiredFields.value.has(field),
      registrationField:
        configuredRegistrationField ??
        registrationField(
          metadataForField?.label ?? field,
          "text",
          field,
          isAttribute,
          false,
          inputType === "email" || inputType === "tel" ? inputType : undefined,
        ),
    };
  });
});

const profileSections = computed<ProfileSection[]>(() => {
  if (!hasRegistrationSteps) {
    return [{ fieldRows: [visibleFields.value] }];
  }

  return registrationSteps.map((step, stepIndex) => ({
    label: step.label,
    fieldRows: groupFieldsIntoRows(
      visibleFields.value.filter(
        (field) => field.registrationField.step === stepIndex,
      ),
    ),
  }));
});

const profileRules = computed<FormRules>(() => {
  const editableFields = visibleFields.value.filter((field) => field.editable);
  return createRegistrationFieldRules(
    editableFields.map((field) => field.registrationField),
    t,
  );
});

const currentLang = computed({
  get(): string {
    const value = baseAttributes.value.langCode;
    return typeof value === "string" ? value : "de";
  },
  set(value: string): void {
    void updateLanguage(value);
  },
});

watch(
  () => userStateLoadingState.value.loaded,
  (loaded) => {
    if (loaded) initEditForm();
  },
);

onMounted(() => {
  void loadData();
  void loadUserEditableFields();
});

function toInputType(type: string | undefined): InputType | undefined {
  if (type === "phone") return "tel";
  return type === "text" ||
    type === "email" ||
    type === "date" ||
    type === "tel" ||
    type === "country"
    ? type
    : undefined;
}

function toStringValue(value: unknown): string {
  return typeof value === "string" || typeof value === "number"
    ? String(value)
    : "";
}

function profileFieldControlId(field: string): string {
  return `profile-field-${field.replaceAll(/[^a-zA-Z0-9_-]/g, "-")}`;
}

function openProfilePictureFileDialog(): void {
  profilePictureInput.value?.click();
}

function groupFieldsIntoRows(fields: ProfileField[]): ProfileField[][] {
  return fields.reduce<ProfileField[][]>((rows, field, index) => {
    if (index % 2 === 0) {
      rows.push([field]);
    } else {
      rows[rows.length - 1]?.push(field);
    }
    return rows;
  }, []);
}

function initEditForm(): void {
  const currentProfile = profile.value;
  if (!userStateLoadingState.value.loaded || !currentProfile) return;

  const attributes = { ...(currentProfile.extras?.attributes ?? {}) };
  if (attributes.address === undefined && attributes.address1 !== undefined) {
    attributes.address = attributes.address1;
  }
  if (attributes.city === undefined && attributes.address2 !== undefined) {
    attributes.city = attributes.address2;
  }
  for (const field of LEGACY_ALIAS_FIELDS) {
    delete attributes[field];
  }

  profileEditingUser.value = {
    username: currentProfile.username,
    email: currentProfile.email,
    attributes,
  };
  editFormInitial.value = { ...editForm.value };
}

function startEditing(): void {
  initEditForm();
  isEditing.value = true;
}

function updateProfileEditingUser(user: Partial<AccountsTypes.User>): void {
  profileEditingUser.value = user;
}

function submitProfileForm(): void {
  void profileFormRef.value?.submit();
}

function cancelEdit(): void {
  initEditForm();
  profileFormRef.value?.clearValidate();
  isEditing.value = false;
}

async function loadUserEditableFields(): Promise<void> {
  try {
    const response = await SystemConfiguration.getSystemSettings({
      path: { setting: "userEditableFields" },
    });
    const fields = response.data?.userEditableFields;
    if (fields && typeof fields === "object") {
      userEditableFields.value = fields as Record<string, boolean>;
    }
  } catch {
    // System settings may be unavailable; no fields are then editable.
  }
}

async function saveProfile(): Promise<void> {
  saveLoading.value = true;
  try {
    const body: Record<string, unknown> = {};
    const attributes: Record<string, unknown> = {};

    for (const [field, editable] of Object.entries(userEditableFields.value)) {
      if (!editable || !allowedFields.value.has(field)) continue;
      const metadata = fieldMetadata.value[field];
      const editedValue = editForm.value[field];
      const value = normaliseValueForSave(
        editedValue,
        field,
        registrationFieldsByProperty.value[field],
      );
      const initialValue = normaliseValueForSave(
        editFormInitial.value[field],
        field,
        registrationFieldsByProperty.value[field],
      );
      if (value === initialValue) continue;

      if (metadata?.isAttribute ?? true) {
        attributes[field] = value;
      } else {
        body[field] = value;
      }
    }

    if (
      Object.keys(body).length === 0 &&
      Object.keys(attributes).length === 0
    ) {
      isEditing.value = false;
      return;
    }
    if (Object.keys(attributes).length > 0) body.attributes = attributes;

    const response = await Accounts.patchUser({
      path: { userId: userData.value.id },
      body: body as AccountsTypes.PatchUserData["body"],
    });
    store.commit("user/userStatePatched", response.data);

    editFormInitial.value = { ...editForm.value };
    isEditing.value = false;
    notify("success", t("changesSavedSuccessfully"), "success");
  } catch {
    notify("error", t("genericError"), "error");
  } finally {
    saveLoading.value = false;
  }
}

async function updateLanguage(value: string): Promise<void> {
  if (!userStateLoadingState.value.loaded || value === currentLang.value)
    return;

  const langCode = value.toLowerCase();
  locale.value = langCode;
  document.documentElement.setAttribute("lang", langCode);

  try {
    const response = await Accounts.patchUser({
      path: { userId: userData.value.id },
      body: { attributes: { langCode } },
    });
    store.commit("user/userStatePatched", response.data);
  } catch {
    notify("error", t("genericError"), "error");
  }
}

async function uploadUserProfile(event: Event): Promise<void> {
  const input = event.target;
  if (!(input instanceof HTMLInputElement)) return;
  const file = input.files?.[0];
  if (!file) return;

  if (file.size > 512 * 1024) {
    notify("error", t("fileSizeError", { sizeMb: "0.5" }), "error");
    input.value = "";
    return;
  }

  try {
    await Accounts.postUserPicture({
      path: { userId: userData.value.id },
      body: { file },
    });
    store.commit("reloadProfilePicture");
    notify("success", "fileUploaded", "success");
  } catch {
    notify("error", t("genericError"), "error");
  } finally {
    input.value = "";
  }
}

async function deleteProfilePicture(): Promise<void> {
  deletePictureLoading.value = true;
  try {
    await store.dispatch("deleteProfilePicture");
    notify("success", "", "success");
  } catch {
    notify("error", t("genericError"), "error");
  } finally {
    deletePictureLoading.value = false;
  }
}

async function takePicture(): Promise<void> {
  if (!camera.value) return;

  const snapshot = camera.value.capture();
  const [metadata = "", encodedImage = ""] = snapshot.split(",");
  const byteString = metadata.includes("base64")
    ? atob(encodedImage)
    : decodeURI(encodedImage);
  const mimeType = metadata.split(":")[1]?.split(";")[0] ?? "";
  const bytes = new Uint8Array(byteString.length);
  for (let index = 0; index < byteString.length; index += 1) {
    bytes[index] = byteString.charCodeAt(index);
  }

  try {
    await Accounts.postUserPicture({
      path: { userId: userData.value.id },
      body: { file: new Blob([bytes], { type: mimeType }) },
    });
    store.commit("reloadProfilePicture");
    cameraVisible.value = false;
    notify("success", "fileUploaded", "success");
  } catch {
    notify("error", t("genericError"), "error");
  }
}

async function setMarketingConsent(value: boolean): Promise<void> {
  userLoadState.value = { ...userLoadState.value, loading: true };
  try {
    await Accounts.patchUser({
      path: { userId: userData.value.id },
      body: { marketingConsent: value },
    });
    if (userLoadState.value.result) {
      userLoadState.value = {
        ...userLoadState.value,
        loading: false,
        result: { ...userLoadState.value.result, marketingConsent: value },
      };
    }
  } catch {
    userLoadState.value = { ...userLoadState.value, loading: false };
    notify("error", t("genericError"), "error");
  }
}

async function loadData(): Promise<void> {
  void store.dispatch("user/loadUserState", {
    id: userData.value.id,
    keepCache: false,
  });

  userLoadState.value = { ...stateLoading };
  try {
    const response = await Accounts.searchUsers({
      body: { page: 1, pageSize: 1, users: [userData.value.id] },
    });
    const user = response.data?.result?.[0];
    userLoadState.value = user
      ? { ...stateLoaded, result: user }
      : { ...stateError, result: null };
  } catch {
    userLoadState.value = { ...stateError, result: null };
  }
}

function normaliseValueForSave(
  value: unknown,
  property: string,
  field: RegistrationField | undefined,
): unknown {
  if (
    (property === "birthyear" || field?.type === "year") &&
    typeof value === "string"
  ) {
    return value.slice(0, 4);
  }
  return value === undefined ? "" : value;
}

function notify(
  title: string,
  text: string,
  notificationType: "success" | "error",
): void {
  store.commit("addNewNotification", { title, text, notificationType });
}
</script>

<style scoped>
.user-details {
  position: relative;
}

.profile-settings-form {
  margin-bottom: 1.5rem;
}

.profile-picture-settings {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  width: 100%;
}

.visually-hidden {
  block-size: 1px;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  inline-size: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
}

.profile-fields-header {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  margin-bottom: 0.5rem;
}

@media (max-width: 767px) {
  .profile-picture-settings {
    align-items: stretch;
    flex-direction: column;
  }
}
</style>
